import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Table, Button, Select, DatePicker, Modal, Form, Input, InputNumber,
  Checkbox, Tag, Popconfirm, message, Tooltip, Space, Statistic, Row, Col, Empty,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, CopyOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getBranches } from '../api/branches';
import {
  getExpenseSchedules, createExpenseSchedule, updateExpenseSchedule,
  setExpenseScheduleStatus, deleteExpenseSchedule, copyExpenseSchedule,
} from '../api/expenseSchedules';
import useAuthStore from '../store/authStore';
import { apiErrorMessage } from '../utils/apiError';
import { numberSorter, textSorter } from '../utils/tableSorters';

const { Option } = Select;

// Şube seçiciyi yalnızca süper adminler görür; şube müdürü zaten kendi
// şubesine kilitli, seçmesi anlamsız (Ödemeler sayfasıyla aynı kural).
const SUBE_SECEBILEN = ['developer_admin', 'super_admin'];

const paraFormat = (v) =>
  new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(v || 0));

const ExpenseSchedule = () => {
  const { user } = useAuthStore();
  const subeSecebilir = SUBE_SECEBILEN.includes(user?.role);

  const [ay, setAy]             = useState(dayjs());
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(user?.branch_id ?? null);
  const [items, setItems]       = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [form] = Form.useForm();

  const [kopyaOpen, setKopyaOpen] = useState(false);
  const [kopyaAy, setKopyaAy]     = useState(dayjs().subtract(1, 'month'));
  const [kopyalaniyor, setKopyalaniyor] = useState(false);

  useEffect(() => {
    if (!subeSecebilir) return;
    getBranches({ per_page: 100 })
      .then(res => {
        const liste = res.data?.data || res.data || [];
        setBranches(liste);
        if (!branchId && liste.length) setBranchId(liste[0].id);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subeSecebilir]);

  // ay ve branchId dependency'de: ikisi de varsayılan parametre olarak
  // kullanılıyor, listeden çıkarılırsa ay değişince eski ay yüklenir
  // (stale closure — bkz. CLAUDE.md).
  const yukle = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getExpenseSchedules({
        year:  ay.year(),
        month: ay.month() + 1,
        ...(branchId ? { branch_id: branchId } : {}),
      });
      setItems(data.items || []);
      setSummary(data.summary || null);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Çizelge yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [ay, branchId]);

  useEffect(() => { yukle(); }, [yukle]);

  const modalAc = (satir = null) => {
    setDuzenlenen(satir);
    form.setFieldsValue(satir
      ? {
          day: satir.day,
          description: satir.description,
          amount: Number(satir.amount),
          is_recurring: !!satir.is_recurring,
          note: satir.note || '',
        }
      : { day: 1, description: '', amount: null, is_recurring: true, note: '' });
    setModalOpen(true);
  };

  const kaydet = async () => {
    const degerler = await form.validateFields();
    setKaydediliyor(true);
    try {
      if (duzenlenen) {
        await updateExpenseSchedule(duzenlenen.id, degerler);
        message.success('Satır güncellendi.');
      } else {
        await createExpenseSchedule({
          ...degerler,
          year:  ay.year(),
          month: ay.month() + 1,
          ...(branchId ? { branch_id: branchId } : {}),
        });
        message.success('Satır eklendi.');
      }
      setModalOpen(false);
      yukle();
    } catch (e) {
      if (e?.errorFields) return;   // form doğrulaması
      message.error(apiErrorMessage(e, 'Kaydedilemedi.'));
    } finally {
      setKaydediliyor(false);
    }
  };

  const durumDegistir = async (satir, alan, deger) => {
    try {
      await setExpenseScheduleStatus(satir.id, { [alan]: deger });
      yukle();
    } catch (e) {
      message.error(apiErrorMessage(e, 'İşaretlenemedi.'));
    }
  };

  const sil = async (id) => {
    try {
      await deleteExpenseSchedule(id);
      message.success('Satır silindi.');
      yukle();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Silinemedi.'));
    }
  };

  const kopyala = async () => {
    setKopyalaniyor(true);
    try {
      const { data } = await copyExpenseSchedule({
        from_year:  kopyaAy.year(),
        from_month: kopyaAy.month() + 1,
        year:       ay.year(),
        month:      ay.month() + 1,
        ...(branchId ? { branch_id: branchId } : {}),
      });
      message.success(data.message || 'Kopyalandı.');
      setKopyaOpen(false);
      yukle();
    } catch (e) {
      message.error(apiErrorMessage(e, 'Kopyalanamadı.'));
    } finally {
      setKopyalaniyor(false);
    }
  };

  const columns = [
    {
      title: 'Gün',
      dataIndex: 'day',
      width: 70,
      sorter: numberSorter('day'),
      defaultSortOrder: 'ascend',
      render: (g) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{g}</span>,
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      sorter: textSorter('description'),
      render: (metin, satir) => (
        <div>
          <div>{metin}</div>
          {satir.note && <div className="text-xs text-gray-400">{satir.note}</div>}
          {!satir.is_recurring && (
            <Tag color="default" style={{ marginTop: 4 }}>Tek seferlik</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      width: 140,
      align: 'right',
      sorter: numberSorter('amount'),
      render: (t) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{paraFormat(t)} ₺</span>
      ),
    },
    {
      title: 'Para Hazır',
      dataIndex: 'ready_at',
      width: 130,
      align: 'center',
      render: (deger, satir) => (
        <Tooltip title={deger ? dayjs(deger).format('DD.MM.YYYY HH:mm') : 'Parayı ayırdığınızda işaretleyin'}>
          <Checkbox
            checked={!!deger}
            // Ödendi işaretliyken "para hazır" kaldırılamaz: ödeme yapıldıysa
            // para zaten ayrılmıştı, aksi hâli anlamsız bir satır bırakır.
            disabled={!!satir.paid_at}
            onChange={(e) => durumDegistir(satir, 'ready', e.target.checked)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Ödendi',
      dataIndex: 'paid_at',
      width: 190,
      render: (deger, satir) => (
        <Space direction="vertical" size={0}>
          <Checkbox
            checked={!!deger}
            onChange={(e) => durumDegistir(satir, 'paid', e.target.checked)}
          >
            {deger ? dayjs(deger).format('DD.MM.YYYY') : 'Ödenmedi'}
          </Checkbox>
          {deger && satir.paid_by && (
            <span className="text-xs text-gray-400" style={{ paddingLeft: 24 }}>
              {satir.paid_by.name} {satir.paid_by.surname}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: '',
      key: 'islem',
      width: 90,
      align: 'right',
      render: (_, satir) => (
        <Space size={4}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => modalAc(satir)} />
          <Popconfirm
            title="Satırı sil?"
            description={satir.paid_at ? 'Bu satır ödendi olarak işaretli.' : undefined}
            onConfirm={() => sil(satir.id)}
            okText="Sil" cancelText="İptal" okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ÖDENENDEN değil, PARASI AYRILANDAN düşülüyor.
  //
  // Sorulan soru "ne kadar borcum kaldı" değil, "ödeme günleri gelmeden ne
  // kadar daha toplamam gerekiyor". Para hazır olduktan sonra ödeme zaten
  // günü gelince yapılıyor; parası ayrılmış bir kalem artık toplanacak
  // listesinde değil. Ödenen tutar ayrı kutuda duruyor.
  const toplanacak = summary ? summary.total - summary.ready_total : 0;

  return (
    <div>
      <Card
        title="Ödeme Çizelgesi"
        extra={
          <Space wrap>
            {subeSecebilir && (
              <Select
                value={branchId}
                onChange={setBranchId}
                style={{ minWidth: 170 }}
                placeholder="Şube"
              >
                {branches.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
              </Select>
            )}
            <DatePicker
              picker="month"
              value={ay}
              onChange={(d) => d && setAy(d)}
              format="MMMM YYYY"
              allowClear={false}
            />
            <Button icon={<CopyOutlined />} onClick={() => setKopyaOpen(true)}>
              Başka aydan kopyala
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => modalAc()}>
              Satır Ekle
            </Button>
          </Space>
        }
      >
        {summary && (
          <Row gutter={16} className="mb-4">
            <Col xs={12} md={6}>
              <Statistic title="Toplam" value={paraFormat(summary.total)} suffix="₺" />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Parası ayrılan" value={paraFormat(summary.ready_total)} suffix="₺" />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={`Ödenen (${summary.paid_count}/${summary.count})`}
                value={paraFormat(summary.paid_total)}
                suffix="₺"
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Statistic
                title={
                  <Tooltip title="Parası henüz ayrılmamış kalemlerin toplamı. Ödeme günleri gelmeden toplanması gereken tutar.">
                    <span style={{ borderBottom: '1px dotted #bfbfbf', cursor: 'help' }}>
                      Toplanacak
                    </span>
                  </Tooltip>
                }
                value={paraFormat(toplanacak)}
                suffix="₺"
                valueStyle={{ color: toplanacak > 0 ? '#cf1322' : '#3f8600' }}
              />
            </Col>
          </Row>
        )}

        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          pagination={false}
          size="middle"
          scroll={{ x: 'max-content' }}
          rowClassName={(satir) => (satir.paid_at ? 'opacity-60' : '')}
          locale={{
            emptyText: (
              <Empty
                description={`${ay.format('MMMM YYYY')} için çizelge boş`}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button icon={<CopyOutlined />} onClick={() => setKopyaOpen(true)}>
                  Başka aydan kopyala
                </Button>
              </Empty>
            ),
          }}
        />
      </Card>

      <Modal
        open={modalOpen}
        title={duzenlenen ? 'Satırı Düzenle' : 'Yeni Satır'}
        onCancel={() => setModalOpen(false)}
        onOk={kaydet}
        confirmLoading={kaydediliyor}
        okText="Kaydet"
        cancelText="İptal"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="description" label="Açıklama"
            rules={[{ required: true, message: 'Açıklama gerekli.' }]}
          >
            <Input placeholder="Örn. Dükkan kira, Şirket kredi kartı" />
          </Form.Item>

          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="day" label="Ayın günü"
                rules={[{ required: true, message: 'Gün gerekli.' }]}
              >
                <InputNumber min={1} max={31} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="amount" label="Tutar (₺)"
                rules={[{ required: true, message: 'Tutar gerekli.' }]}
              >
                <InputNumber
                  min={0} step={100} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => `${v}`.replace(/\./g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="note" label="Not (isteğe bağlı)">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>

          <Form.Item name="is_recurring" valuePropName="checked">
            <Checkbox>
              Her ay tekrarlansın
              <div className="text-xs text-gray-400" style={{ marginLeft: 24 }}>
                İşaretliyse gelecek ayın çizelgesine kendiliğinden eklenir.
                Tutarı o ay değiştirebilirsiniz.
              </div>
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={kopyaOpen}
        title="Başka aydan kopyala"
        onCancel={() => setKopyaOpen(false)}
        onOk={kopyala}
        confirmLoading={kopyalaniyor}
        okText="Kopyala"
        cancelText="İptal"
      >
        <p className="text-sm text-gray-500">
          Seçtiğiniz ayın satırları <b>{ay.format('MMMM YYYY')}</b> çizelgesine eklenir.
          Mevcut satırlar silinmez; aynı gün ve açıklamaya sahip satır varsa atlanır.
          Tutarlar gelir, <b>"para hazır" ve "ödendi" işaretleri gelmez</b>.
        </p>
        <DatePicker
          picker="month"
          value={kopyaAy}
          onChange={(d) => d && setKopyaAy(d)}
          format="MMMM YYYY"
          allowClear={false}
          style={{ width: '100%' }}
        />
      </Modal>
    </div>
  );
};

export default ExpenseSchedule;
